import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, Workflow, MousePointerClick } from 'lucide-react';

/**
 * Public-facing navy header for the ProcureOne Ultra homepage.
 *
 * Design brief (Feb 2026 — Founder):
 *   • Background: subtle navy gradient (#041B36 → #071F4A)
 *   • Gold accent: #D4AF37 (no purple, no heavy gradients)
 *   • Left  — Plus Ultra lotus mark + "PLUS ULTRA INNOVATION PRESENTS"
 *              product line "ProcureOne Ultra" (ProcureOne white, Ultra gold)
 *   • Center — motto in gold italic (hidden on mobile)
 *   • Right — Workflow Demo (transparent + gold border), Interactive Demo
 *              (gold background + navy text). Both radius 6–8px, no pills.
 *
 * Navigation links, demo routes and functionality are untouched.
 */
export default function PublicHeader({ onOpenSidebar }) {
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 text-white shadow-md"
      style={{ background: 'linear-gradient(90deg, #041B36 0%, #071F4A 100%)' }}
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 lg:py-2.5 grid grid-cols-[auto_1fr_auto] items-center gap-3 lg:gap-6">
        {/* LEFT — mobile hamburger + brand block */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onOpenSidebar}
            aria-label="Open navigation menu"
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
            data-testid="header-sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-4 leading-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded-sm min-w-0"
            data-testid="public-header-brand"
          >
            <span
              className="inline-flex items-center justify-center shrink-0 rounded-full bg-white p-[2px] shadow-[0_0_0_1px_#D4AF37,0_0_18px_-4px_rgba(212,175,55,0.55)]"
            >
              <img
                src="/plus-ultra-logo.webp"
                alt="Plus Ultra Innovation"
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-contain"
                data-testid="public-header-logo-img"
              />
            </span>
            <div className="min-w-0">
              <div className="hidden sm:block text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold whitespace-nowrap">
                Plus Ultra Innovation Presents
              </div>
              <div className="text-lg sm:text-xl lg:text-2xl font-black tracking-tight leading-none mt-0.5 whitespace-nowrap">
                <span className="text-white">ProcureOne</span>{' '}
                <span className="text-[#D4AF37]">Ultra</span>
              </div>
            </div>
          </Link>
        </div>

        {/* CENTER — motto in gold italic (hidden below md so mobile stays clean) */}
        <div
          className="hidden md:flex justify-center text-center px-2"
          data-testid="header-motto"
        >
          <p className="text-xs lg:text-sm font-semibold italic text-[#D4AF37] leading-snug max-w-md">
            &ldquo;We help co-op housing soc to spend their money transparently&rdquo;
          </p>
        </div>

        {/* RIGHT — demo CTAs (6px radius, enterprise style, no pills) */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/demo/lifecycle/buyer')}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md border border-[#D4AF37] bg-transparent text-sm font-semibold text-white hover:bg-[#D4AF37]/10 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
            data-testid="header-workflow-demo-btn"
          >
            <Workflow className="w-4 h-4" />
            <span className="hidden sm:inline">Workflow Demo</span>
            <span className="sm:hidden">Workflow</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/demo')}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md bg-[#D4AF37] text-[#041B36] text-sm font-bold hover:bg-[#c19d2f] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            data-testid="header-interactive-demo-btn"
          >
            <MousePointerClick className="w-4 h-4" />
            <span className="hidden sm:inline">Interactive Demo</span>
            <span className="sm:hidden">Demo</span>
          </button>
        </div>
      </div>
    </header>
  );
}
